import { Link } from 'react-router-dom';
import {
  FiArrowDownLeft,
  FiArrowUpRight,
  FiCheck,
  FiClock,
  FiHelpCircle,
  FiLayers,
  FiLoader,
  FiShuffle,
  FiX,
} from 'react-icons/fi';
import CopyButton from './CopyButton';
import TxDetailsPageHeader from './TxDetailsPageHeader';
import { formatRelativeTime, formatUtcTimestamp } from '../../utils/formatTime';

function AddressValue({ address }) {
  if (!address) return <span>—</span>;

  return (
    <span className="eth-tx-addr-wrap">
      <Link to={`/tx/${encodeURIComponent(address)}`} className="eth-tx-link">
        {address}
      </Link>
      <CopyButton text={address} className="eth-tx-copy" />
    </span>
  );
}

function DetailRow({ label, children, hint }) {
  return (
    <div className="eth-tx-row">
      <div className="eth-tx-row__label">
        <span className="eth-tx-hint" title={hint || label}>
          <FiHelpCircle aria-hidden />
        </span>
        {label}
      </div>
      <div className="eth-tx-row__value">{children}</div>
    </div>
  );
}

function activityMeta(activityType) {
  if (activityType === 'deposit') {
    return {
      label: 'Deposit',
      title: 'Deposit Details',
      subtitle: 'Funds credited to a Polyrion wallet.',
      Icon: FiArrowDownLeft,
      tone: 'deposit',
      steps: ['Requested', 'Processing', 'Completed'],
      failLabel: 'Rejected',
    };
  }

  if (activityType === 'withdrawal') {
    return {
      label: 'Withdrawal',
      title: 'Withdrawal Details',
      subtitle: 'Funds requested from a Polyrion wallet.',
      Icon: FiArrowUpRight,
      tone: 'withdrawal',
      steps: ['Requested', 'Processing', 'Completed'],
      failLabel: 'Rejected',
    };
  }

  return {
    label: 'Trade',
    title: 'Trade Details',
    subtitle: 'Market trade settled on the Polyrion prediction network.',
    Icon: FiShuffle,
    tone: 'trade',
    steps: ['Requested', 'Processing', 'Completed'],
    failLabel: 'Failed',
  };
}

function statusClass(status) {
  if (status === 'success') return 'is-success';
  if (status === 'failed') return 'is-failed';
  return 'is-pending';
}

function adminStatusLabel(details = {}) {
  const raw = String(
    details.admin_status ||
      details.withdrawal_status ||
      details.deposit_status ||
      details.trade_status ||
      ''
  ).toLowerCase();

  if (!raw) return null;

  if (raw === 'rejected') return 'Rejected';
  if (raw === 'cancelled' || raw === 'canceled') return 'Cancelled';
  if (raw === 'failed') return 'Failed';
  if (['completed', 'approved', 'success', 'settled', 'won', 'lost'].includes(raw)) return 'Completed';
  if (raw === 'pending') return 'Pending';
  if (raw === 'processing') return 'Processing';

  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Build stepper steps with done / current / fail + connector fill state. */
function buildTimeline(labels, status, failLabel = 'Failed') {
  const isFailed = status === 'failed';
  const isSuccess = status === 'success';
  // pending => Processing current; success => all done; failed => last failed
  const activeIndex = isSuccess ? labels.length : isFailed ? labels.length - 1 : 1;

  return labels.map((label, index) => {
    const isLast = index === labels.length - 1;
    let state = 'upcoming';

    if (isFailed && isLast) {
      state = 'fail';
    } else if (isSuccess || index < activeIndex) {
      state = 'done';
    } else if (index === activeIndex) {
      state = 'current';
    }

    return {
      key: `${label}-${index}`,
      label: isFailed && isLast ? failLabel : label,
      state,
      connectorFilled: index < labels.length - 1 && (isSuccess || index < activeIndex),
      showConnector: index < labels.length - 1,
      stepNumber: index + 1,
    };
  });
}

function StepIcon({ state }) {
  if (state === 'done') return <FiCheck aria-hidden />;
  if (state === 'fail') return <FiX aria-hidden />;
  if (state === 'current') return <FiLoader className="prc-stepper__spin" aria-hidden />;
  return null;
}

function PrcPlatformTxDetails({ display, loading, error }) {
  const activityType = display?.activityType || 'trade';
  const meta = activityMeta(activityType);
  const { Icon } = meta;
  const details = display?.details || {};

  if (loading) {
    return (
      <main className="sections eth-tx-page prc-activity-page">
        <TxDetailsPageHeader title={meta.title} />
        <div className="stx-skeleton stx-skeleton-panel" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !display) {
    return (
      <main className="sections eth-tx-page prc-activity-page">
        <TxDetailsPageHeader title={meta.title} />
        <div className="stx-error">
          <p>{error || 'Transaction not found.'}</p>
          <Link to="/" className="eth-tx-link">
            Back to home
          </Link>
        </div>
      </main>
    );
  }

  const timeline = buildTimeline(meta.steps, display.status, meta.failLabel);
  const statusLabel = adminStatusLabel(details) || display.statusLabel;
  const isFailed = display.status === 'failed';
  const isSuccess = display.status === 'success';
  const StatusIcon = isSuccess ? FiCheck : isFailed ? FiX : FiClock;

  return (
    <main className={`sections eth-tx-page prc-activity-page prc-activity-page--${meta.tone}`}>
      <TxDetailsPageHeader title={meta.title} />
      <p className="prc-activity-subtitle">{meta.subtitle}</p>

      <section className={`prc-activity-hero prc-activity-hero--${meta.tone}`}>
        <div className="prc-activity-hero__glow" aria-hidden />

        <div className="prc-activity-hero__main">
          <div className="prc-activity-hero__top">
            <span className={`prc-activity-hero__icon prc-activity-hero__icon--${meta.tone}`}>
              <Icon aria-hidden />
            </span>
            <div className="prc-activity-hero__top-text">
              <span className={`prc-activity-badge prc-activity-badge--${meta.tone}`}>
                {meta.label}
              </span>
              <span className={`prc-activity-status ${statusClass(display.status)}`}>
                <StatusIcon aria-hidden />
                {statusLabel}
              </span>
            </div>
          </div>

          <h2 className="prc-activity-hero__action">{display.actionText}</h2>

          <div className="prc-activity-hero__meta">
            <span className="prc-activity-hero__time">
              <FiClock aria-hidden />
              {formatRelativeTime(display.blockTime)}
              <span className="prc-activity-hero__dot" aria-hidden />
              {formatUtcTimestamp(display.blockTime)}
            </span>
            {display.hash ? (
              <span className="prc-activity-hero__hash" title={display.hash}>
                <code>{`${display.hash.slice(0, 10)}…${display.hash.slice(-6)}`}</code>
                <CopyButton text={display.hash} className="eth-tx-copy" />
              </span>
            ) : null}
          </div>
        </div>

        <div className={`prc-activity-hero__side prc-activity-hero__side--${meta.tone}`}>
          <span className="prc-activity-amount-label">Amount</span>
          <strong className="prc-activity-amount">{display.value?.amount || '—'}</strong>
          <span className="prc-activity-hero__side-note">{meta.label} value</span>
        </div>
      </section>

      <section className="prc-stepper-card" aria-label="Transaction progress">
        <div className="prc-stepper-card__head">
          <span>Progress</span>
          <span className="prc-stepper-card__hint">
            {isSuccess
              ? 'All steps completed'
              : isFailed
                ? statusLabel === 'Rejected'
                  ? 'Rejected by admin'
                  : 'Transaction failed'
                : 'In progress'}
          </span>
        </div>

        <ol className="prc-stepper">
          {timeline.map((step) => (
            <li
              key={step.key}
              className={`prc-stepper__item is-${step.state}${step.connectorFilled ? ' has-filled-line' : ''}`}
            >
              <div className="prc-stepper__track">
                <span className="prc-stepper__dot" aria-hidden>
                  <StepIcon state={step.state} />
                  {step.state === 'upcoming' ? (
                    <span className="prc-stepper__num">{step.stepNumber}</span>
                  ) : null}
                </span>
                {step.showConnector ? <span className="prc-stepper__line" aria-hidden /> : null}
              </div>
              <span className="prc-stepper__label">{step.label}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="prc-activity-stats">
        <article className="prc-activity-stat">
          <span className="prc-activity-stat__label">Method</span>
          <strong>{display.method || meta.label}</strong>
        </article>
        <article className="prc-activity-stat">
          <span className="prc-activity-stat__label">Block</span>
          <strong>
            {display.block != null ? (
              <Link to={`/tx/${display.block}`} className="eth-tx-link eth-tx-link--bold">
                #{display.block}
              </Link>
            ) : (
              'Pending'
            )}
          </strong>
        </article>
        <article className="prc-activity-stat">
          <span className="prc-activity-stat__label">Confirmations</span>
          <strong>{display.confirmationsLabel || '—'}</strong>
        </article>
        <article className="prc-activity-stat">
          <span className="prc-activity-stat__label">Network fee</span>
          <strong>{display.fee?.amount || '0.00 PRC'}</strong>
        </article>
      </section>

      <section className="eth-tx-card prc-activity-card">
        <div className="prc-activity-card__head">
          <FiLayers aria-hidden />
          <span>Transaction overview</span>
        </div>

        <DetailRow label="Transaction Hash:" hint="Unique PolyrionScan identifier">
          <span className="eth-tx-hash-wrap">
            <code className="eth-tx-mono">{display.hash}</code>
            <CopyButton text={display.hash} className="eth-tx-copy" />
          </span>
        </DetailRow>

        <DetailRow label="Status:">
          <span className={`prc-activity-status ${statusClass(display.status)}`}>
            <StatusIcon aria-hidden />
            {statusLabel}
          </span>
        </DetailRow>

        <DetailRow label="Timestamp:">
          <span className="eth-tx-inline eth-tx-time-row">
            <FiClock aria-hidden />
            <span>{formatRelativeTime(display.blockTime)}</span>
            <span className="eth-tx-time-sep">|</span>
            <span>{formatUtcTimestamp(display.blockTime)}</span>
          </span>
        </DetailRow>

        <div className="eth-tx-divider" />

        <DetailRow label="From:">
          <AddressValue address={display.from} />
        </DetailRow>

        <DetailRow label="To:">
          <AddressValue address={display.to} />
        </DetailRow>

        <div className="eth-tx-divider" />

        <DetailRow label="Value:">
          <span className="eth-tx-value-row">{display.value?.amount || '—'}</span>
        </DetailRow>

        {details.payment_method ? (
          <DetailRow label="Payment method:" hint="Original Polyrion payment channel">
            <span className="prc-activity-pill">{details.payment_method}</span>
          </DetailRow>
        ) : null}

        {details.market ? (
          <DetailRow label="Market:" hint="Prediction market for this trade">
            <span>{details.market}</span>
          </DetailRow>
        ) : null}

        {details.outcome ? (
          <DetailRow label="Outcome:">
            <span className="prc-activity-pill prc-activity-pill--outcome">{details.outcome}</span>
          </DetailRow>
        ) : null}

        {details.description ? (
          <DetailRow label="Description:">
            <span>{details.description}</span>
          </DetailRow>
        ) : null}

        {details.external_reference ? (
          <DetailRow label="Reference:" hint="Internal Polyrion reference">
            <code className="eth-tx-mono">{details.external_reference}</code>
          </DetailRow>
        ) : null}
      </section>
    </main>
  );
}

export default PrcPlatformTxDetails;
